// Styles (Vuetify styles should go first to ease overriding)
import 'vuetify/styles';
import '@/assets/scss/app.scss';
// Globals
import '@/globals.js';

import * as Sentry from '@sentry/vue';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
// Basic
import { createApp } from 'vue';
import { createGtag } from 'vue-gtag';
// External Libraries
import { pageTitle } from 'vue-page-title';
import VueScrollTo from 'vue-scrollto';
import VueClipboard from 'vue3-clipboard';
// Vuetify 3
import { createVuetify } from 'vuetify';
import * as components from 'vuetify/components';
import * as directives from 'vuetify/directives';

import Layout from '@/components/layout.vue';
import vIntersect from '@/directives/Intersect.js';
// Directives
import vOutsideClick from '@/directives/outside-click.js';
import EventBus from '@/event-bus.js';
import ApiClientPlugin from '@/plugins/api-client';
import BrandPlugin from '@/plugins/brand';
// Plugins
import DataLayerPlugin from '@/plugins/data-layer';
import EchoPlugin from '@/plugins/echo';
import NumbersPlugin from '@/plugins/numbers';
import router from '@/routes.js';
import store from '@/store';

const app = createApp(Layout);

const dsn = import.meta.env.VITE_APP_SENTRY_DSN;

if (dsn) {
    Sentry.init({
        app,
        dsn,
        integrations: [Sentry.browserTracingIntegration({ router })],
        // Tracing
        tracesSampleRate: 1.0,
    });
}

// Vuetify 3 settings
const BBTheme = {
    dark: false,
    colors: {
        primary: '#51c1f7',
        secondary: '#ed2434',
        accent: '#1b313c',
        error: '#F44336',
        warning: '#ffeb3b',
        info: '#2196F3',
        success: '#4CAF50',
    },
};
const vuetify = createVuetify({
    components,
    directives,
    theme: {
        defaultTheme: 'BBTheme',
        themes: { BBTheme },
    },
    defaults: {
        VAutocomplete: { variant: 'outlined' },
        VTextField: { variant: 'outlined' },
        VCombobox: { variant: 'outlined' },
        VSelect: { variant: 'outlined' },
        VTextarea: { variant: 'outlined' },
    },
});

// vue-page-title related
let title = '';
let titleSuffix = 'BuyerBridge';

// Google Analytics
const gtag = createGtag({
    tagId: import.meta.env.VITE_APP_GOOGLE_ANALYTICS_ID,
});

app.use(router);
app.use(store);
app.use(vuetify);
app.use(
    pageTitle({
        mixin: true,
        setTitleMethod: (pageTitle) => {
            title = pageTitle;
            document.title = `${pageTitle} | ${titleSuffix}`;
        },
    })
);
app.use(gtag);
app.use(VueClipboard, { autoSetContainer: true });
app.use(VueScrollTo);
app.use(DataLayerPlugin);
app.use(ApiClientPlugin);
app.use(BrandPlugin);
app.use(EchoPlugin);
app.use(NumbersPlugin);

// Filters
app.config.globalProperties.$filters = {
    formatPhoneNumber(val, country) {
        if (val && country) {
            return parsePhoneNumberFromString(val, country).formatNational();
        }
    },
    capitalize(val) {
        return val.charAt(0).toUpperCase() + val.slice(1);
    },
    truncate(val, length = 35) {
        if (val !== null && val !== undefined && val.length >= length) {
            return val.substring(0, length) + '...';
        } else {
            return val;
        }
    },
    machineToFriendlyName(v) {
        if (typeof v !== 'string') return '';
        let splitStr = v.toLowerCase().split('_');
        for (let i = 0; i < splitStr.length; i++) {
            // You do not need to check if i is larger than splitStr length, as your for does that for you
            // Assign it back to the array
            splitStr[i] = splitStr[i].charAt(0).toUpperCase() + splitStr[i].substring(1);
        }
        // Directly return the joined string
        return splitStr.join(' ');
    },
    camelCaseToWords(v) {
        const parts = v.split(/(?=[A-Z])/);
        // Ensure the first word is capitalized to support camel (proper) and pascal simultaneously
        if (parts[0]) {
            parts[0] = parts[0].charAt(0).toUpperCase() + parts[0].substring(1);
        }
        return parts.join(' ');
    },
    lowercase(v) {
        return v.toLowerCase();
    },
    formatCurrency(val) {
        if (val !== null && val !== undefined) {
            return val.toLocaleString('en-US', {
                style: 'currency',
                currency: 'USD',
            });
        }
    },
};

// Global Properties
app.config.globalProperties.$flash = function (message, color = 'blue') {
    EventBus.emit('flash', { message, color });
};
app.config.globalProperties.$setTitleSuffix = (suffix) => {
    titleSuffix = suffix;
    document.title = `${title} | ${titleSuffix}`;
};

app.directive('outside-click', vOutsideClick);
app.directive('intersect', vIntersect);

app.mount('#app');
